import { useEffect, useState } from "react";
import { WarehouseOutwardModel } from "../model/warehouse_outward_model";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { fetchPut } from "../../content_panel/services/serviceClient";
import LoadingWidget from "../../../../context_providers/modal/loader";
import { getOutwardDestinationLabel } from "../warehouse_helper";
import { InwardAttachmentUpload } from "../inward/InwardAttachmentUpload";

type Props = {
  open: boolean;
  onClose: () => void;
  outward: WarehouseOutwardModel;
  onSuccess: () => void;
};

export default function UploadInvModal({
  open,
  onClose,
  outward,
  onSuccess,
}: Props) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);

  useEffect(() => {
    if (!open) {
      setAttachments([]);
    }
  }, [open]);

  const getTotalValue = () => {
    return (
      outward?.items?.reduce(
        (sum: number, i: any) =>
          sum + (i.itemPrice || 0) * (i.requestedQty || 0),
        0,
      ) || 0
    );
  };

  /* ---------------- MARK AS DISPATCHED ---------------- */
  const handleDispatch = async () => {
    try {
      setLoading(true);

      if (attachments.length === 0) {
        showToast({
          type: ToastType.warning,
          text: "Please upload invoice",
        });
        setLoading(false);
        return;
      }

      const formData = new FormData();

      // IMPORTANT → key name must match backend (same pattern as delivered API)
      attachments.forEach((file) => {
        formData.append("attachments", file);
      });

      const res = await fetchPut(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/upload-invoice-inter/${outward.id}`,
        formData,
        { "Content-Type": "multipart/form-data" }, // ⭐ critical
      );

      if (res.success) {
        showToast({
          type: ToastType.success,
          text: "Outward marked as dispatched",
        });
        onSuccess?.();
        onClose();
      } else {
        showToast({
          type: ToastType.error,
          text: res.error || "Invoice upload failed",
        });
      }
    } catch (error: any) {
      console.error("Dispatch failed:", error);
      showToast({
        type: ToastType.error,
        text: error.message || "Dispatch failed",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      id="warehouse-finance-upload-inv-modal"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-40 overflow-y-auto p-4 sm:p-8"
    >
      <div className="relative w-full sm:max-w-lg bg-white shadow-lg rounded-lg flex flex-col wh-modal-xs">
        {/* Header */}
        <div className="border-b px-6 py-4">
          <h2 className="text-lg font-semibold">Upload Invoice</h2>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-700">
              Please upload invoice before dispatching this outward order.
            </p>
          </div>

          {/* Order Summary */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3 text-sm">
            <p className="flex justify-between">
              <strong className="text-gray-700">Order:</strong>
              <span className="text-gray-900 font-semibold">
                {outward.type === "SO" ? outward.order_id : outward.id}
              </span>
            </p>

            <p className="flex justify-between">
              <strong className="text-gray-700">Destination:</strong>
              <span className="text-gray-900 font-semibold">
                {getOutwardDestinationLabel(outward)}
              </span>
            </p>

            <p className="flex justify-between">
              <strong className="text-gray-700">Total Value:</strong>
              <span className="text-blue-600 font-bold text-lg">
                ₹{getTotalValue().toLocaleString()}
              </span>
            </p>
          </div>

          {/* Upload */}
          <div
            id="warehouse-finance-upload-inv-attachment-wrapper"
            className="space-y-2"
          >
            <label className="block text-sm font-medium text-gray-700">
              Invoice Attachment *
            </label>

            <InwardAttachmentUpload
              accept="application/pdf,image/jpeg,image/png"
              maxSizeMB={10}
              files={attachments}
              multiple={false}
              onChange={setAttachments}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex gap-3">
          <button
            id="warehouse-finance-upload-inv-cancel-btn"
            onClick={onClose}
            className="flex-1 h-12 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
          >
            Cancel
          </button>

          <button
            id="warehouse-finance-upload-inv-submit-btn"
            onClick={handleDispatch}
            disabled={loading || attachments.length === 0}
            className={`flex-1 h-12 rounded-lg font-medium shadow-lg ${
              loading || attachments.length === 0
                ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          >
            {loading ? "Dispatching..." : "Mark as Ready"}
          </button>
        </div>
      </div>

      {loading && <LoadingWidget />}
    </div>
  );
}
